import React, { forwardRef, useImperativeHandle, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ClaimsTrendMetricsCard from './ClaimsTrendMetricsCard';
import SurplusDeficitMetricsCard from './SurplusDeficitMetricsCard';
import CurrentEnrollmentMetricsCard from './CurrentEnrollmentMetricsCard';
import { getSheetNameByWorkBookName } from './newHomeHelper';
import { getMetricValue } from './claimsTrendMetricsCardHelper';
import HighCostClaimantsMetricsCard from './HighCostClaimantsMetricsCard';
import MemberCostShareMetricsCard from './MemberCostShareMetricsCard';
import GrossClaimsTrendMetricsCard from './GrossClaimsTrendMetricsCard';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { DEFAULT_VISIBLE_METRICS } from 'common/Constant';

import { IconChevronDown, IconChevronUp, IconCheck, IconX } from '@tabler/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAllMetrics, buildMetricsFilters, getFilterHash, getAllMetricsCacheKey } from 'features/metrics/metricsActions';

const PerformanceMetrics = forwardRef((props,ref) => {
  const { onMetricsChange, hidden = false } = props;
  const dispatch = useDispatch();
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const metricsCache = useSelector((state) => state.metrics.cache);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const defaultsSeeded = useSelector((state) => state.globalFilters?.defaultsSeeded);
  const filterHash = getFilterHash(buildMetricsFilters(appliedFilters));

  const isMetricTemporarilyUnavailable = (metric) =>
    metric.requiresPriorPeriod && appliedFilters?.enablePriorComparison === false;

  const hasMetricData = (metric) => {
    if (isMetricTemporarilyUnavailable(metric)) {
      return false;
    }
    const cacheKey = currentTenantCd ? getAllMetricsCacheKey(currentTenantCd, filterHash) : null;
    // No tenant yet — optimistically show cards
    if (!cacheKey) return false;
    const cached = metricsCache[cacheKey];
    // No cache entry yet, or actively loading — show cards (loading state)
    if (!cached || cached.loading) return true;
    // If request failed, keep tile visible so card can render its own error state.
    if (cached.error) return false;
    // Data present but empty {} from a previous silent failure — treat as no data
    const hasData = cached.data &&
      typeof cached.data === 'object' &&
      !Array.isArray(cached.data) &&
      Object.keys(cached.data).length > 0;
    if (!hasData) return false;
    if (metric.defaultMeasureName) {
      const val = getMetricValue(cached.data, metric.defaultMeasureName);
      return val !== null && val !== undefined;
    }
    return true;
  };

  // Add metrics config
  const metricsConfig = [
    {
      id: 'totalClaims',
      label: 'Total Claims Trend',
      component: ClaimsTrendMetricsCard,
      sheetName: getSheetNameByWorkBookName('Total Claims Trend'),
      workBookName: 'Total Claims Trend',
      defaultMeasureName: 'Metric - Total Claims PEPM % Diff',
      requiresPriorPeriod: true,
      width: 290,
      height: 148,
    },
    {
      id: 'medClaims',
      label: 'Medical Claims Trend',
      component: ClaimsTrendMetricsCard,
      sheetName: getSheetNameByWorkBookName('Med Claims Trend'),
      workBookName: 'Med Claims Trend',
      defaultMeasureName: 'Metric - Med Claims PEPM % Diff',
      requiresPriorPeriod: true,
      width: 290,
      height: 148,
    },
    {
      id: 'rxClaims',
      label: 'Rx Claims Trend',
      component: ClaimsTrendMetricsCard,
      sheetName: getSheetNameByWorkBookName('Rx Claims Trend'),
      workBookName: 'Rx Claims Trend',
      defaultMeasureName: 'Metric - Rx Claims PEPM % Diff',
      requiresPriorPeriod: true,
      width: 290,
      height: 148,
    },
    {
      id: 'surplusDeficit',
      label: 'Surplus / Deficit',
      component: SurplusDeficitMetricsCard,
      sheetName: 'Metrics - Gross And Surplus',
      workBookName: 'Metrics v2',
      defaultMeasureName: 'Surplus Deficit Curr',
      width: 290,
      height: 148,
    },
    {
      id: 'currentEnrollment',
      label: 'Current Enrollment',
      component: CurrentEnrollmentMetricsCard,
      sheetName: 'Metrics - Enrollments',
      workBookName: 'Metrics v2',
      defaultMeasureName: 'Members',
      width: 290,
      height: 148,
    },
    {
      id: 'highCostClaimants',
      label: 'High Cost Claimants',
      component: HighCostClaimantsMetricsCard,
      sheetName: 'Metrics - HCC',
      workBookName: 'Metrics v2',
      defaultMeasureName: '# HCC per 1000 Current',
      width: 290,
      height: 148,
    },
    {
      id: 'memberCostShare',
      label: 'Member Cost Share Trend',
      component: MemberCostShareMetricsCard,
      sheetName: 'Metrics - Cost Share',
      workBookName: 'Metrics v2',
      defaultMeasureName: 'Member Cost Share Curr',
      width: 290,
      height: 148,
    },
    {
      id: 'grossCost',
      label: 'Gross Claims Cost',
      component: GrossClaimsTrendMetricsCard,
      sheetName: 'Metrics - Gross And Surplus',
      workBookName: 'Metrics v2',
      defaultMeasureName: 'Gross Cost PEPM Diff',
      requiresPriorPeriod: true,
      width: 290,
      height: 148,
    }
  ];

  // Track which metrics are visible
  const [visibleMetrics, setVisibleMetrics] = useState(DEFAULT_VISIBLE_METRICS);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [metricsOrder, setMetricsOrder] = useState(metricsConfig.map((m) => m.id));

  useImperativeHandle(ref,()=>({
    // When opening modal, initialize temp states
    handleOpenModal(){
      setTempVisibleMetrics(visibleMetrics);
      setTempMetricsOrder(metricsOrder);
      setOpenModal(true);
    },
    
    hasVisibleMetrics(){
      const visibleCount = metricsOrder.filter((id) => {
        const metric = metricsConfig.find((m) => m.id === id);
        return visibleMetrics[id] && hasMetricData(metric);
      }).length;
      return visibleCount > 0;
    }
  }),[visibleMetrics, metricsOrder, metricsConfig, hasMetricData])

  // Add temporary states for modal editing
  const [tempVisibleMetrics, setTempVisibleMetrics] = useState(visibleMetrics);
  const [tempMetricsOrder, setTempMetricsOrder] = useState(metricsOrder);
  const [saveError, setSaveError] = useState(false);

  // Filter metrics based on visibility and data availability
  const visibleMetricObjects = metricsOrder.map((id) => metricsConfig.find((m) => m.id === id)).filter((m) => visibleMetrics[m.id] && hasMetricData(m));

  const collapsedCount = Math.min(4, visibleMetricObjects.length);

  const displayedMetrics = expanded ? visibleMetricObjects : visibleMetricObjects.slice(0, collapsedCount);

  // Save handler for modal
  const handleSaveMetrics = () => {
    try {
      const finalVisible = { ...tempVisibleMetrics };
      metricsConfig.forEach((m) => {
        if (!isMetricTemporarilyUnavailable(m) && !hasMetricData(m)) {
          finalVisible[m.id] = false;
        }
      });
      setVisibleMetrics(finalVisible);
      setMetricsOrder(tempMetricsOrder);
      if (currentTenantCd) {
        const savedVisibleMetrics = JSON.parse(localStorage.getItem('visibleMetrics')) || {};
        savedVisibleMetrics[currentTenantCd] = { ...finalVisible };
        localStorage.setItem('visibleMetrics', JSON.stringify(savedVisibleMetrics));
      }
      localStorage.setItem('metricsOrder', JSON.stringify(tempMetricsOrder));
      setSaveError(false);
      setOpenModal(false);
    } catch (e) {
      setSaveError(true);
    }
  };

  // Cancel handler: reset temp states, keep modal open
  const handleCancelMetrics = () => {
    setTempVisibleMetrics(visibleMetrics);
    setTempMetricsOrder(metricsOrder);
    setOpenModal(false);
    // Modal stays open
  };

  // On mount, restore visibleMetrics and metricsOrder from localStorage if available
  React.useEffect(() => {
    try {
      const savedOrder = localStorage.getItem('metricsOrder');
      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);
        setMetricsOrder(parsed);
        setTempMetricsOrder(parsed);
      }
    } catch (_) { /* corrupted — use defaults */ }
  }, []);

  React.useEffect(() => {
    setPrefsLoaded(false);
    if (!currentTenantCd || !defaultsSeeded) return;
    try {
      const savedVisibleMetrics = localStorage.getItem('visibleMetrics');
      if (savedVisibleMetrics) {
        const parsedVisibleMetrics = JSON.parse(savedVisibleMetrics)[currentTenantCd] || DEFAULT_VISIBLE_METRICS;
        setVisibleMetrics(parsedVisibleMetrics);
        setTempVisibleMetrics(parsedVisibleMetrics);
      }
    } catch (_) {}
    setPrefsLoaded(true);
  }, [currentTenantCd, defaultsSeeded]);

  // Skip the VDS call while the section is hidden; fetch once it is shown again.
  React.useEffect(() => {
    if (hidden || !currentTenantCd || !defaultsSeeded) return;
    dispatch(fetchAllMetrics());
  }, [hidden, currentTenantCd, defaultsSeeded, dispatch]);

  const hasAnyVisibleMetric = React.useMemo(
    () => metricsOrder.some((id) => {
      const metric = metricsConfig.find((m) => m.id === id);
      return visibleMetrics[id] && hasMetricData(metric);
    }),
    [metricsOrder, visibleMetrics, metricsConfig, hasMetricData]
  );

  // Compute available/unavailable metrics for the drawer
  const allOrderedMetrics = tempMetricsOrder.map((metricId) => metricsConfig.find((m) => m.id === metricId));
  const availableMetrics = allOrderedMetrics.filter((m) => hasMetricData(m));
  const unavailableMetrics = allOrderedMetrics.filter((m) => !hasMetricData(m));
  const hasAvailableMetrics = availableMetrics.length > 0;
  const metricsCacheMap = useSelector((state) => state.metrics.cache);

  const hasRealData = (data) =>{
    return !!data && typeof data === 'object' && Object.values(data).some((v) => v !== null &&(typeof v !== 'object' || Object.keys(v).length > 0));
  }

  const tenantHadDataInCache = React.useMemo(()=>{
    if(!metricsCacheMap || !currentTenantCd) return false;
    const prefix = `${currentTenantCd}_`;
    return Object.entries(metricsCacheMap).some(([key, entry]) => {
      if(!key.startsWith(prefix)) return false;
      const has = key.slice(prefix.length);
      if(has.includes('_')) return false;
      return hasRealData(entry?.data);
    });
  }, [metricsCacheMap, currentTenantCd]);

  const tenantData = hasAvailableMetrics || tenantHadDataInCache;

  React.useEffect(() => {
    if (!prefsLoaded) return;
    if (onMetricsChange) {
      onMetricsChange(hasAnyVisibleMetric, hasAvailableMetrics, tenantData);
    }
  }, [hasAnyVisibleMetric, hasAvailableMetrics, onMetricsChange, prefsLoaded]);

  return (
    // Performance Metrics Box
    <Box
      sx={{
        width: '100%',
        height: 'auto',
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        position: 'relative',
        transition: 'height 0.3s',
        overflow: 'visible',
        minWidth: '-webkit-fill-available',
      }}>
      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 672 },
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#FFF',
          }
        }}>
        {/* Top close button section: 672px x 72px with 24px padding */}
        <Box
          sx={{
            width: '100%',
            height: '72px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            py: 3,
            px: {  xs: 2, sm: 6, md: 3 },
            boxSizing: 'border-box',
            flexShrink: 0,
            borderBottom: '1px solid var(--Gray---Line-Stroke, #71767A)',
            background: 'var(--white-bi, #FFF)',
          }}>
          <IconButton
            onClick={() => setOpenModal(false)}
            aria-label="close"
            sx={{
              display: 'flex',
              width: '24px',
              height: '24px',
              padding: 0,
              justifyContent: 'center',
              alignItems: 'center',
              color: '#000',
            }}>
            <IconX size={24} stroke={2} />
          </IconButton>
        </Box>

        {/* Scrollable content area */}
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            px: {  xs: 2, sm: 6, md: 3 },
            pt: '24px',
            pb: '40px',
          }}>
          {/* Title and description in flex column with 16px gap */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pb: '24px' }}>
            <Typography
              sx={{
                color: '#000',
                fontFamily: 'Rubik',
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                letterSpacing: '0.2px',
                width: '100%',
                height: '30px',
              }}>
              Modify Metrics
            </Typography>
            {availableMetrics.length > 0 && (
              <Typography
                sx={{
                  color: '#000',
                  fontFamily: 'Noto Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%',
                  width: '100%',
                }}>
                Select which metrics you would like to display on your dashboard.
              </Typography>
            )}
          </Box>

          {/* Metric checkboxes list - only metrics with data */}
          {availableMetrics.length > 0 && (
                  <Box sx={{ pb: '40px', width: '100%' }}>
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: '16px',
                      border: '1px solid var(--Gray---Line-Stroke, #71767A)',
                      background: 'var(--white-bi, #FFF)',
                      p: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '24px',
                    }}>
                    {availableMetrics.map((metric) => (
                      <Box
                        key={metric.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          width: '100%',
                          height: '20px',
                          m: 0,
                          p: 0,
                        }}>
                        <Box sx={{ 
                          display: 'flex', 
                          height: '20px', 
                          alignItems: 'center', 
                          gap: '8px' }}>
                          <Checkbox
                            icon={
                              <Box sx={{ 
                                display: 'flex', 
                                width: 20, 
                                height: 20, 
                                padding: '2.5px', 
                                flexDirection: 'column', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                borderRadius: '5px', 
                                border: '2px solid #2F54A5', 
                                boxSizing: 'border-box', 
                                flexShrink: 0 }} />
                            }
                            checkedIcon={
                              <Box sx={{ 
                                width: 20, 
                                height: 20, 
                                borderRadius: '3px', 
                                backgroundColor: '#2F54A5', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                flexShrink: 0 
                              }}>
                                <IconCheck size={16} color="#fff" stroke={2} style={{ flexShrink: 0 }} />
                              </Box>
                            }
                            sx={{
                              p: 0, m: 0, height: '20px', width: '20px',
                            }}
                            checked={tempVisibleMetrics[metric.id]}
                            onChange={() => {
                              setTempVisibleMetrics((prev) => ({
                                ...prev,
                                [metric.id]: !prev[metric.id]
                              }));
                            }}
                          />
                          <Typography sx={{
                             color: '#000', 
                             fontFamily: 'Noto Sans', 
                             fontSize: '14px', 
                             fontWeight: 600, 
                             lineHeight: '150%' }}>{metric.label}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  </Box>
                )}

                {unavailableMetrics.length > 0 && (
                  <Box sx={{ 
                    pb: '40px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '16px', 
                    width: '100%' }}>
                    <Typography
                      sx={{
                        color: 'var(--Gray-Darkest---Body-Text, #121A22)',
                        fontFamily: 'Noto Sans',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '150%',
                        width: '100%',
                        mb: 0,
                      }}>
                      {hasAvailableMetrics
                        ? 'The following metrics are either not available for your account or additional data is still being collected.'
                        : 'The following metrics are unavailable because the required data is not included in your organization\'s current configuration.'}
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: '16px',
                        border: '1px solid var(--Gray---Line-Stroke, #71767A)',
                        background: 'var(--white-bi, #FFF)',
                        p: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                      }}>
                      {unavailableMetrics.map((metric) => (
                        <Box
                          key={metric.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            alignSelf: 'stretch',
                            width: '576px',
                            height: '20px',
                          }}>
                          <Typography
                            sx={{
                              color: 'var(--black-heading-text-icons, #000)',
                              fontFamily: 'Noto Sans',
                              fontSize: '14px',
                              fontStyle: 'normal',
                              fontWeight: 600,
                              lineHeight: '150%',
                            }}>
                            {metric.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

          {saveError && (
            <Box sx={{ mt: 2, display: 'flex', gap: 0.5 }}>
              <Typography sx={{ 
                fontSize: '14px', 
                fontWeight: 500, 
                color: '#D32F2F' }}>Save unsuccessful!</Typography>
              <Typography sx={{ 
                fontSize: '14px', 
                fontWeight: 500, 
                color: '#121A22' }}>Please try again!</Typography>
            </Box>
          )}
        </Box>

        {/* Bottom sticky button section */}
      {hasAvailableMetrics && (
        <Box
          sx={{
            width: '100%',
            height: '92px',
            flexShrink: 0,
            marginTop: 'auto',
            display: 'flex',
            py: 3,
            px: { xs: 2, sm: 6, md: 3 },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            borderTop: '1px solid var(--Gray---Line-Stroke, #71767A)',
            background: 'var(--white-bi, #FFF)',
            boxSizing: 'border-box',
          }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            width: '100%', 
            justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancelMetrics}
              sx={{
                display: 'flex',
                flex: { xs: 1, sm: 'none' },
                width: { xs: 'auto', sm: '84px' },
                height: '44px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '24px',
                border: '1px solid var(--Blue-600---Primary-CTA, #2F54A5)',
                background: 'var(--white-bi, #FFF)',
                color: 'var(--Blue-600---Primary-CTA, #2F54A5)',
                textAlign: 'center',
                fontFamily: 'Noto Sans',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                textTransform: 'capitalize',
                '&:hover': {
                  backgroundColor: '#D8E6FF',
                  borderColor: '#001A47',
                  color: '#001A47',
                },
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveMetrics}
              sx={{
                display: 'flex',
                width: 'auto',
                flex: { xs: 1, sm: 'none' },
                minWidth: { xs: 0, sm: '140px' },
                height: '44px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '24px',
                background: 'var(--Blue-600---Primary-CTA, #2F54A5)',
                color: 'var(--white-bi, #FFF)',
                textAlign: 'center',
                fontFamily: 'Noto Sans',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '150%',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  background: 'var(--Blue-600---Primary-CTA, #2F54A5)',
                  boxShadow: 'none',
                },
              }}>
              Save Changes
            </Button>
          </Box>
        </Box>
      )}
      </Drawer>
      {/* Unmounted while hidden so the cards don't trigger metric fetches */}
      <Collapse in={!hidden} unmountOnExit timeout={300} sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {displayedMetrics.length > 0 && (
      <Box
        sx={{
          display: 'grid',
          width: '100%',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          marginTop: '24px',
          marginBottom: '24px',
          gap: '24px',
        }}>
        {displayedMetrics.map((metric, index) => (
          <Box
            key={metric.id + (expanded ? '_expanded' : '')}
            sx={{
              flexShrink : 0
            }}

            >
            {React.createElement(metric.component, {
              sheetName: metric.sheetName,
              workBookName: metric.workBookName,
              height: metric.height,
              index: index,
              enablePriorComparison: appliedFilters?.enablePriorComparison !== false,
              key: metric.id + (expanded ? '_expanded' : '')
            })}
          </Box>
        ))}
      </Box>
      )}
      {(visibleMetricObjects.filter(hasMetricData).length > 4) && <Typography
        sx={{
          color: '#2F54A5',
          fontFamily: 'Noto Sans',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
          cursor: 'pointer',
          '&:hover': { color: '#068DF1' },
        }}
        onClick={() => {
          setExpanded(prev => !prev);
        }}
      >
        Show {expanded ? 'Less':'More'}
        {expanded ? 
          <IconChevronUp size={16} color='currentColor' style={{ marginLeft: 4 }}/> :
          <IconChevronDown size={16} color='currentColor' style={{ marginLeft: 4 }}/>
        }
      </Typography>}
      </Box>
      </Collapse>
    </Box>
  );
});

export default PerformanceMetrics;
